import ComparisonTable from "components-for/display/comparison-table";
import Page from "constants/page";
import Qs from "qs";
import useAgents from "hooks-for/agents/use-agents";
import { FunctionComponent, memo, useCallback } from "react";
import { IProps } from "./agents-table.types";
import { generatePath } from "react-router-dom";
import { useInteractionQueryParameters } from "hooks-for/filters/use-interaction-query-parameters";
import "./agents-table.scss";

/**
 * A table of agents with data relevant for assessing performance.
 * @returns A table of agents.
 */
const AgentsTable: FunctionComponent<IProps> = () => {
    const filters = useInteractionQueryParameters();
    const { data: agents, isLoading: areAgentsLoading } = useAgents({ limit: 1500 });

    /**
     * Navigate to the agent review screen and display the agent from the clicked row. Also apply the query parameters
     * that have been selected for this screen.
     * @param agentId   Clicked agent
     */
    const goToAgent = useCallback(
        (agentId: string) => {
            return generatePath(`${Page.Agent}?:search`, {
                search: Qs.stringify(
                    { reviewAgentId: agentId, ...filters },
                    { arrayFormat: "repeat", skipNulls: true, encode: false }
                ),
            });
        },
        [filters]
    );

    return (
        <ComparisonTable
            getGroupId={({ agentId }) => agentId}
            groupBy="agentId"
            groups={areAgentsLoading ? [] : agents ?? []}
            groupTitle="Agent"
            headingProperties={{ Name: ({ name }) => name, Email: ({ email }) => email }}
            hasHorizontalScroll
            className="comparison-agent-table"
            getRowLink={goToAgent}
            interactionType={filters.interactionType}
        >
            {({ email, name }) => (
                <div className="comparison-table__agent">
                    <span className="comparison-table__name">{name}</span>
                    {typeof email === "string" && <span className="comparison-table__email">({email})</span>}
                </div>
            )}
        </ComparisonTable>
    );
};

export default memo(AgentsTable);
